import { useState, useEffect } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { settingsApi, type NotificationPreferences } from '@/services/settings';
import { ApiClientError } from '@/services/api';
import { cn } from '@/utils/cn';

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  id: string;
  label: string;
}

function ToggleSwitch({ enabled, onChange, disabled, id, label }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-labelledby={`${id}-label`}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={cn(
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        enabled ? 'bg-primary-600' : 'bg-gray-200'
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          enabled ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </button>
  );
}

interface NotificationRowProps {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function NotificationRow({ id, title, description, enabled, onChange, disabled }: NotificationRowProps) {
  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex-1 pr-4">
        <label id={`${id}-label`} htmlFor={id} className="font-medium text-gray-900 cursor-pointer">
          {title}
        </label>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <ToggleSwitch
        id={id}
        label={title}
        enabled={enabled}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Notification settings component
 * Manages email notification preferences
 */
export default function NotificationSettings() {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch notification preferences
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const prefs = await settingsApi.getNotificationPreferences();
      setPreferences(prefs);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to load notification preferences.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!preferences) return;

    const previousPreferences = { ...preferences };
    const updatedPreferences = { ...preferences, [key]: value };
    setPreferences(updatedPreferences);
    setIsSaving(true);
    setError(null);

    try {
      await settingsApi.updateNotificationPreferences(updatedPreferences);
      setSuccessMessage('Preferences saved');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      // Revert on error
      setPreferences(previousPreferences);
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to save preferences. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Notifications"
        description="Choose what email notifications you receive"
        action={
          successMessage && (
            <span className="text-sm text-green-600">{successMessage}</span>
          )
        }
      />

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="divide-y divide-gray-100">
        <NotificationRow
          id="transactionAlerts"
          title="Transaction alerts"
          description="Get notified about large or unusual transactions"
          enabled={preferences?.transactionAlerts ?? false}
          onChange={(value) => handleToggle('transactionAlerts', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="weeklyDigest"
          title="Weekly digest"
          description="Receive a weekly summary of your spending and income"
          enabled={preferences?.weeklyDigest ?? false}
          onChange={(value) => handleToggle('weeklyDigest', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="monthlyReport"
          title="Monthly report"
          description="Get a detailed monthly financial report"
          enabled={preferences?.monthlyReport ?? false}
          onChange={(value) => handleToggle('monthlyReport', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="budgetAlerts"
          title="Budget alerts"
          description="Get notified when you approach or exceed budget limits"
          enabled={preferences?.budgetAlerts ?? false}
          onChange={(value) => handleToggle('budgetAlerts', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="connectionIssues"
          title="Connection issues"
          description="Get notified when there are problems with your linked accounts"
          enabled={preferences?.connectionIssues ?? true}
          onChange={(value) => handleToggle('connectionIssues', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="securityAlerts"
          title="Security alerts"
          description="Important notifications about your account security (recommended)"
          enabled={preferences?.securityAlerts ?? true}
          onChange={(value) => handleToggle('securityAlerts', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="productUpdates"
          title="Product updates"
          description="Learn about new features and improvements"
          enabled={preferences?.productUpdates ?? false}
          onChange={(value) => handleToggle('productUpdates', value)}
          disabled={isSaving}
        />

        <NotificationRow
          id="marketingEmails"
          title="Marketing emails"
          description="Occasional tips and offers to help you manage your finances"
          enabled={preferences?.marketingEmails ?? false}
          onChange={(value) => handleToggle('marketingEmails', value)}
          disabled={isSaving}
        />
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (preferences) {
              const allOff: NotificationPreferences = {
                transactionAlerts: false,
                weeklyDigest: false,
                monthlyReport: false,
                budgetAlerts: false,
                connectionIssues: false,
                securityAlerts: false,
                productUpdates: false,
                marketingEmails: false,
              };
              setPreferences(allOff);
              settingsApi.updateNotificationPreferences(allOff);
            }
          }}
        >
          Disable all notifications
        </Button>
      </div>
    </Card>
  );
}
